import { getCommonTrackingParams } from 'Common/logging/tokens';
import { SEARCH_V9_DISTANCE_EXPANSION_STRINGS } from 'Src/utils/constants';

/**
 * Returns the alt providers array of objects based on provider and locations response
 * @param {object} provider - contains provider info
 * @param {array} locations - array of location objects, can be provider locations or user selection
 * @param {string} customerCode - represents a customer
 * @param {object} config - default config of customer
 * @param {object} searchSDK - contains client and config info
 * @returns {Promise<array>} the alternate provider array of objects
 */

export const getAltProviders = async ({
  provider,
  locations,
  customerCode,
  config,
  searchSDK,
  queryParams,
  req
}) => {
  try {
    const searchClient = searchSDK.current;
    const params = {
      context: config.index || customerCode,

      ...getCommonTrackingParams({ config, req }),

      has_availability: true,
      slots_filter: {
        and: {
          and: {
            patient_relationship: ['new']
          }
        }
      },
      filter: {
        and: {
          and: {
            'provider.direct_book_capable': [true]
          }
        },
        not: {
          'provider.id': [provider.id]
        }
      },
      sort: 'relevance,distance,availability_density',
      exclude_from_analytics: true,

      ...queryParams
    };

    // add specialties OR filter if we have specialties
    // e.g. provider.specialties.name:Cardiology#Electrophysiology#Endocrinology
    if (provider.specialties && provider.specialties.length > 0) {
      params.filter.and.or = {
        'provider.specialties.name': provider.specialties.map(
          ({ name }) => name
        )
      };
    }

    // add location if we have one, use coordinates from the first location
    const firstLocationWithCoordinates =
      locations && locations.length > 0
        ? locations.find((l) => l.coordinates?.lat && l.coordinates?.lon)
        : null;
    if (firstLocationWithCoordinates) {
      params.location = `${firstLocationWithCoordinates.coordinates.lat},${firstLocationWithCoordinates.coordinates.lon}`;
    }
    const configuredDistance = config.alternate_providers?.distance;
    if (configuredDistance && typeof configuredDistance === 'number') {
      params.distance = configuredDistance;
    }
    const response = await searchClient.getProviders(params);

    const distanceExpansionMessage = response.data.messages?.distance_expansion;
    const isDistanceExpanded =
      typeof distanceExpansionMessage === 'number' ||
      distanceExpansionMessage ===
        SEARCH_V9_DISTANCE_EXPANSION_STRINGS.DISTANCE_FILTER_REMOVED;

    // If a customer has limited the distance for alternate providers,
    // do not return providers outside of that radius.
    if (configuredDistance && isDistanceExpanded) {
      return [];
    }
    return response.data._result;
  } catch (e) {
    if (req) {
      let error = e;
      if (!(error instanceof Error)) {
        error = new Error(e.error, { cause: e });
      }

      const log = req.getLog();
      log.error(`error fetching alternate providers:`, {
        error
      });
    }
    return [];
  }
};
